import { useMemo } from 'react'
import { useOrg } from '@/contexts/OrgContext'
import { useAuth } from '@/contexts/AuthContext'
import { DEFAULT_FEATURE_FLAGS } from '@/types/feature-flags'
import type { OrgFeatureFlags, FeatureFlagKey } from '@/types/feature-flags'

export function useFeatureFlags(): OrgFeatureFlags {
  const { org, membership } = useOrg()
  const { isSuperAdmin } = useAuth()

  return useMemo(() => {
    // Super Admin always sees everything — literally: every flag true. Bare
    // defaults are NOT enough since default-false sections (agents,
    // presentaciones, tarjeta_presente) would lock the super admin out of
    // planets that the org row has enabled.
    if (isSuperAdmin) {
      return Object.fromEntries(
        Object.keys(DEFAULT_FEATURE_FLAGS).map((k) => [k, true]),
      ) as unknown as OrgFeatureFlags
    }
    // Merge: defaults → org flags → user flags
    // User-level overrides take precedence over org-level
    return {
      ...DEFAULT_FEATURE_FLAGS,
      ...(org?.feature_flags ?? {}),
      ...(membership?.feature_flags ?? {}),
    }
  }, [org?.feature_flags, membership?.feature_flags, isSuperAdmin])
}

export function useFeatureFlag(flag: FeatureFlagKey): boolean {
  return useFeatureFlags()[flag]
}
